import React, { useRef, useEffect, useState } from "react";
import mapboxgl from "mapbox-gl";
import "./Map.css";

import starImg from "./star-48.png";

var polyUtil = require("polyline-encoded");

mapboxgl.accessToken =
  "pk.eyJ1Ijoicm93YW5zdWdkZW4iLCJhIjoiY2s1emNkaWNzMDhjbDNub2JxcGY2MTRmbyJ9.0H5Lis6IIyV7Dgh8lysqiQ";

const Map = ({ activities }) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const hasFlownRef = useRef(false);

  const [lng, setLng] = useState(-4);
  const [lat, setLat] = useState(56);
  const [zoom, setZoom] = useState(6.0);
  const [styleReady, setStyleReady] = useState(false);

  // Create the map once when the component mounts. Route/marker updates are
  // handled in a separate effect below so new Strava data doesn't tear down
  // and rebuild the whole map (which reset the user's pan/zoom and replayed
  // the fly-to animation on every update).
  useEffect(() => {
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      // style: "mapbox://styles/mapbox/streets-v11",
      style: "mapbox://styles/mapbox/satellite-streets-v11",
      center: [lng, lat],
      zoom: zoom,
    });
    mapRef.current = map;

    // Add navigation control (the +/- zoom buttons)
    map.addControl(new mapboxgl.NavigationControl(), "top-right");

    map.on("load", () => {
      // Hide road/tunnel/bridge/ferry network layers so Strava routes stand
      // out, while keeping place-name labels (settlement, country, poi, etc.)
      const roadLayerPattern =
        /^(road|tunnel|bridge)-|^ferry(-auto)?$|^aerialway$/;
      map.getStyle().layers.forEach((layer) => {
        if (roadLayerPattern.test(layer.id)) {
          map.setLayoutProperty(layer.id, "visibility", "none");
        }
      });

      setStyleReady(true);
    });

    map.on("move", () => {
      setLng(map.getCenter().lng.toFixed(4));
      setLat(map.getCenter().lat.toFixed(4));
      setZoom(map.getZoom().toFixed(2));
    });

    // Clean up on unmount
    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- map should only be created once; lng/lat/zoom here are just the initial center/zoom
  }, []);

  // Draw/update routes and markers whenever activities change, reusing the
  // existing map instance instead of recreating it.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !styleReady) return;

    // Clear markers and route layers/sources from the previous render
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];
    map.getStyle().layers.forEach((layer) => {
      if (layer.id.startsWith("route")) {
        map.removeLayer(layer.id);
        map.removeSource(layer.id);
      }
    });

    var arrayLength = activities.length;

    for (var i = 0; i < arrayLength; i++) {
      const polylineEncoded = activities[i]["map"]["summary_polyline"];
      if (polylineEncoded.length !== 0) {
        // Polylines
        const polylineDecoded = polyUtil.decode(polylineEncoded);
        polylineDecoded.forEach((arr) => arr.reverse());
        const routeName = "route" + i;
        map.addSource(routeName, {
          type: "geojson",
          data: {
            type: "Feature",
            properties: {},
            geometry: {
              type: "LineString",
              coordinates: polylineDecoded,
            },
          },
        });
        map.addLayer({
          id: routeName,
          type: "line",
          source: routeName,
          layout: {
            "line-join": "round",
            "line-cap": "round",
          },
          paint: {
            "line-color": "#FF0",
            "line-width": 2,
          },
        });

        // Markers
        const el = document.createElement("div");
        const width = 10;
        const height = 10;
        el.className = "marker";
        el.style.backgroundColor = "yellow";
        el.style.width = `${width}px`;
        el.style.height = `${height}px`;
        el.style.backgroundSize = "100%";
        const startMarker = new mapboxgl.Marker(el)
          .setLngLat(polylineDecoded[0])
          .addTo(map);
        const el2 = document.createElement("div");
        el2.className = "marker";
        el2.style.backgroundColor = "yellow";
        el2.style.width = `${width}px`;
        el2.style.height = `${height}px`;
        el2.style.backgroundSize = "100%";
        const endMarker = new mapboxgl.Marker(el2)
          .setLngLat(polylineDecoded[polylineDecoded.length - 1])
          .addTo(map);
        markersRef.current.push(startMarker, endMarker);
      }
    }

    // Current Location Marker
    if (activities.length > 0) {
      const polylineEncoded = activities[0]["map"]["summary_polyline"];
      if (polylineEncoded !== undefined) {
        const polylineDecoded = polyUtil.decode(polylineEncoded);
        polylineDecoded.forEach((arr) => arr.reverse());
        const currentLocEl = document.createElement("div");
        const width = 24;
        const height = 24;
        currentLocEl.className = "marker";
        currentLocEl.style.backgroundImage = `url(${starImg})`;
        currentLocEl.style.width = `${width}px`;
        currentLocEl.style.height = `${height}px`;
        currentLocEl.style.backgroundSize = "100%";
        const currentLocMarker = new mapboxgl.Marker(currentLocEl)
          .setLngLat(polylineDecoded[polylineDecoded.length - 1])
          .addTo(map);
        markersRef.current.push(currentLocMarker);
      }
    }

    // Fly to the most recent location once, the first time real data
    // arrives, rather than every time activities are refreshed - repeating
    // that on every update would yank the camera away from wherever the
    // user is currently looking.
    if (!hasFlownRef.current && activities.length > 0) {
      const polylineEncoded = activities[0]["map"]["summary_polyline"];
      const polylineDecoded = polyUtil.decode(polylineEncoded);
      const flyTarget = polylineDecoded[polylineDecoded.length - 1];
      const flyTargetSwapped = [flyTarget[1], flyTarget[0]];
      hasFlownRef.current = true;
      setTimeout(() => {
        map.flyTo({
          center: flyTargetSwapped,
          zoom: 11,
          bearing: 0,
          speed: 1, // make the flying slow
          curve: 1, // change the speed at which it zooms out
          easing: (t) => t,
          essential: true,
        });
      }, 1000);
    }
  }, [activities, styleReady]);

  return <div className="map-container" ref={mapContainerRef} />;
};

export { Map };
